import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "./ui/sonner";
import { Loader2, Search, Settings2 } from "lucide-react";

const LENGTHS = [["short", "约 1 分钟", "~1 min"], ["long", "约 2 分钟", "~2 min"]];
const VOICE_ZH = { nova: "Nova（女声·清晰）", alloy: "Alloy（中性）", echo: "Echo（男声）", fable: "Fable（英式）", onyx: "Onyx（低沉男声）", shimmer: "Shimmer（柔和女声）" };

// 晨报偏好：板块 / 项目或小组筛选 / 口播长度 / 音色（按人保存，文字版与语音版同时生效）
export const BriefPrefsDialog = ({ api, zh, open, onClose, onSaved }) => {
  const [meta, setMeta] = useState(null);
  const [p, setP] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/ai/dept-brief/prefs").then(({ data }) => { setMeta(data); setP(data.prefs); }).catch((e) => toast.error(e?.detailText || e.message));
  }, [open]); // eslint-disable-line
  const projects = useMemo(() => (meta?.projects || []).filter((x) => !q || (x.address || "").toLowerCase().includes(q.toLowerCase())), [meta, q]);
  const tog = (k, v) => setP((x) => ({ ...x, [k]: x[k].includes(v) ? x[k].filter((y) => y !== v) : [...x[k], v] }));
  const save = async () => {
    if (!p.sections.length) return toast.error(zh ? "至少选择一个板块" : "Pick at least one section");
    setBusy(true);
    try { const { data } = await api.put("/ai/dept-brief/prefs", p); toast.success(zh ? "偏好已保存，晨报将按新设置生成" : "Saved"); onSaved?.(data.prefs); onClose(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="brief-prefs-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Settings2 className="h-4 w-4 text-indigo-600" />{zh ? "晨报偏好" : "Brief preferences"}</DialogTitle></DialogHeader>
        {!p ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div> : (
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-xs font-bold text-slate-600 mb-1.5">{zh ? "包含板块" : "Sections"}</p>
              <div className="flex flex-wrap gap-1.5">
                {meta.sections.map((s) => (
                  <button key={s.key} data-testid={`brief-pref-sec-${s.key}`} data-on={p.sections.includes(s.key) ? "1" : "0"} onClick={() => tog("sections", s.key)} title={s.hint}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${p.sections.includes(s.key) ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s.zh}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-600 mb-1.5">{zh ? "只看这些小组" : "Teams"} <span className="text-slate-400 font-normal">{zh ? "（不选 = 全部）" : "(none = all)"}</span></p>
              <div className="flex flex-wrap gap-1.5">
                {meta.teams.map((t) => (
                  <button key={t.key} data-testid={`brief-pref-team-${t.key}`} data-on={p.teams.includes(t.key) ? "1" : "0"} onClick={() => tog("teams", t.key)} className={`text-xs px-2.5 py-1 rounded-full border ${p.teams.includes(t.key) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t.name}</button>
                ))}
                {!meta.teams.length && <span className="text-xs text-slate-400">—</span>}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-600 mb-1.5">{zh ? "只看这些项目" : "Projects"} <span className="text-slate-400 font-normal">{zh ? `（已选 ${p.projectIds.length}，不选 = 全部；选了项目则忽略小组）` : `(${p.projectIds.length} selected)`}</span></p>
              <div className="relative mb-1.5"><Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" /><input data-testid="brief-pref-proj-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={zh ? "搜索地址…" : "Search…"} className="w-full text-xs border border-slate-200 rounded-md pl-7 pr-2 py-1.5" /></div>
              <div className="max-h-40 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-50" data-testid="brief-pref-projects">
                {projects.slice(0, 80).map((x) => (
                  <label key={x.id} className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" data-testid={`brief-pref-proj-${x.id}`} checked={p.projectIds.includes(x.id)} onChange={() => tog("projectIds", x.id)} className="accent-indigo-600" />
                    <span className="truncate flex-1">{x.address}</span><span className="text-[10px] text-slate-400">{x.teamName || x.team || ""}</span>
                  </label>
                ))}
                {!projects.length && <p className="text-xs text-slate-400 px-2 py-2">—</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-bold text-slate-600 mb-1.5">{zh ? "口播长度" : "Spoken length"}</p>
                <div className="flex gap-1.5">{LENGTHS.map(([k, z, en]) => <button key={k} data-testid={`brief-pref-len-${k}`} data-on={p.length === k ? "1" : "0"} onClick={() => setP({ ...p, length: k })} className={`text-xs px-2.5 py-1 rounded-full border ${p.length === k ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600"}`}>{zh ? z : en}</button>)}</div>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-600 mb-1.5">{zh ? "播报音色" : "Voice"}</p>
                <select data-testid="brief-pref-voice" value={p.voice} onChange={(e) => setP({ ...p, voice: e.target.value })} className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
                  {meta.voices.map((v) => <option key={v} value={v}>{zh ? VOICE_ZH[v] || v : v}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="brief-pref-cancel" onClick={onClose} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600">{zh ? "取消" : "Cancel"}</button>
              <button data-testid="brief-pref-save" disabled={busy} onClick={save} className="text-xs font-semibold px-4 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-1">{busy && <Loader2 className="h-3 w-3 animate-spin" />}{zh ? "保存并重新生成" : "Save"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
